import { DatabaseTable } from "../models/BaseTable";
import store from "../store/Store";

// If we're running unit tests or integration tests, API requests need to be handled slightly differently
// For postgrest api calls that means using the test schema, for others we probably need to mock the call
const testing = process.env.REACT_APP_TEST_MODE === "test";
/* Standard functions/data types for interacting with postgrest */

// See here for details on the following operators:
// https://postgrest.org/en/stable/references/api/tables_views.html
export enum postgrest_operators {
  eq = "eq",
  gt = "gt",
  gte = "gte",
  lt = "lt",
  lte = "lte",
  neq = "neq",
  like = "like",
  ilike = "ilike",
  match = "match",
  imatch = "imatch",
  in = "in",
  is = "is",
  isdistinct = "isdistinct",
  fts = "fts",
  plfts = "plfts",
  phfts = "phfts",
  wfts = "wfts",
  cs = "cs",
  cd = "cd",
  ov = "ov",
  sl = "sl",
  sr = "sr",
  nxr = "nxr",
  nxl = "nxl",
  adj = "adj",
  not = "not",
  or = "or",
  and = "and",
  all = "all",
  any = "any",
}

export interface WhereClause {
  operator: postgrest_operators;
  column: string;
  value: string;
}

export const getDBToken = () => {
  if (
    process.env.REACT_APP_STATIC_JWT === "true" &&
    typeof process.env.REACT_APP_POSTGREST_JWT !== "undefined"
  ) {
    return process.env.REACT_APP_POSTGREST_JWT;
  } else {
    return store.getState().auth.pgToken;
  }
};

export const runDatabaseFunction = async (
  function_name: string,
  parameters: any,
): Promise<Response> => {
  const url = process.env.REACT_APP_API_URL + "/rpc/" + function_name;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      "Content-Profile": testing ? "test" : "build",
    },
    body: JSON.stringify(parameters),
  });

  return response;
};

export const queryTable = async (
  table: string,
  clauses: WhereClause[] = [],
  select = "",
): Promise<Response> => {
  let url = process.env.REACT_APP_API_URL + "/" + table;
  if (select) {
    url += "?select=" + select;
  }
  if (clauses) {
    if (select) {
      url += "&";
    } else {
      url += "?";
    }
    for (const clause of clauses) {
      (url += clause.column + "=" + clause.operator + "." + clause.value),
        (url += "&");
    }
    url = url.slice(0, -1); // Remove the last &
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      "Accept-Profile": testing ? "test" : "build",
    },
  });

  return response;
};

export const upsertRows = async (
  table: string,
  rows: DatabaseTable[],
): Promise<Response> => {
  const url = process.env.REACT_APP_API_URL + "/" + table;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      Prefer: "resolution=merge-duplicates",
      "Content-Profile": testing ? "test" : "build",
    },
    body: JSON.stringify(rows),
  });

  return response;
};

export const upsertRow = async (
  table: string,
  row: DatabaseTable,
): Promise<Response> => {
  const data = row as any;
  if (typeof data.id !== "number") {
    throw new Error("Table must have a primary key to upsert a single row");
  }
  const url = process.env.REACT_APP_API_URL + "/" + table + "?id=eq." + data.id;

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      "Content-Profile": testing ? "test" : "build",
    },
    body: JSON.stringify(data),
  });

  return response;
};

export const insertIntoTable = async (
  table: string,
  newRow: DatabaseTable,
): Promise<Response> => {
  const url = process.env.REACT_APP_API_URL + "/" + table;
  const data = newRow as any;
  if (typeof data.id === "number") {
    data.id = undefined;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      Prefer: "return=representation",
      "Content-Profile": testing ? "test" : "build",
    },
    body: JSON.stringify(data),
  });

  return response;
};

export const updateTable = async (
  table: string,
  clauses: WhereClause[],
  updates: Map<string, any>,
): Promise<Response> => {
  if (!clauses) {
    throw new Error("Cannot update " + table + " without any where clauses");
  }
  let url = process.env.REACT_APP_API_URL + "/" + table + "?";
  for (const clause of clauses) {
    url += clause.column + "=" + clause.operator + "." + clause.value;
    url += "&";
  }
  url = url.slice(0, -1); // Remove the last &

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      "Content-Profile": testing ? "test" : "build",
    },
    body: JSON.stringify(updates),
  });

  return response;
};

export const deleteTableRow = async (
  table: string,
  pkVal: string,
  pkCol = "id",
): Promise<Response> => {
  const url: string =
    process.env.REACT_APP_API_URL + "/" + table + "?" + pkCol + "=eq." + pkVal;
  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      Authorization: "Bearer " + getDBToken(),
      "Content-Type": "application/json",
      "Content-Profile": testing ? "test" : "build",
    },
  });
  return response;
};

/* End standard functions for interacting with postgrest */
